import {Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle} from "@mui/material";
import Grid from "@mui/material/Grid";
import {forwardRef, useEffect, useImperativeHandle, useState} from "react";

import MyCheckbox from "../../components/MyCheckbox";
import MyMultiSelectCheckboxes from "../../components/MyMultiSelectCheckboxes";
import MySelect from "../../components/MySelect";
import MyTextField from "../../components/MyTextField";
import {DATA_TYPES, FACILITY_FIELDS, MODAL_MODES, VIEW_TYPES} from "../../constants";
import {useBusinessUnitCodes} from "../../hooks/useBusinessUnitCodes";
import {useCountries} from "../../hooks/useCountries";
import {useCurrentUser} from "../../hooks/useCurrentUser";
import {useFacilityCreate} from "../../hooks/useFacilityCreate";
import {useFacilityUpdate} from "../../hooks/useFacilityUpdate";
import {useStates} from "../../hooks/useStates";
import {useGolferOrderActionTypes} from "../../hooks/useGolferOrderActionTypes";
import FacilityUsersTable from "./FacilityUsersTable";

const getInitialFormData = () => {
    const initialFormData = {};

    FACILITY_FIELDS.forEach((field) => {
        initialFormData[field.name] = {};
        switch (field.viewType) {
            case VIEW_TYPES.TEXT:
                initialFormData[field.name].value = "";
                break;
            case VIEW_TYPES.SINGLE_SELECT:
                initialFormData[field.name].value = "";
                initialFormData[field.name].options = [];
                break;
            case VIEW_TYPES.MULTI_SELECT:
                initialFormData[field.name].value = [];
                initialFormData[field.name].options = [];
                break;
            case VIEW_TYPES.CHECKBOX:
                initialFormData[field.name].value = field.defaultValue;
                break;
        }
    });

    return initialFormData;
};

const FacilityModal = forwardRef(({refetchFacilities}, ref) => {
    const [resetKey, setResetKey] = useState(0);
    const [open, setOpen] = useState(false);
    const [errorMessages, setErrorMessages] = useState({});
    const [facilityDetail, setFacilityDetail] = useState(null);
    const [formData, setFormData] = useState(getInitialFormData);
    const [mode, setMode] = useState(MODAL_MODES.CREATE);
    const [loading, setLoading] = useState(false);

    useImperativeHandle(
        ref,
        () => ({
            handleOpen,
        }),
        []
    );
    useEffect(() => {
        if (facilityDetail) {
            setFormData((prev) => {
                let newFormData = {...prev};
                FACILITY_FIELDS.forEach((field) => {
                    switch (field.dataType) {
                        case DATA_TYPES.STRING:
                        case DATA_TYPES.TIMESTAMP:
                            newFormData[field.name] = {
                                ...prev[field.name],
                                value: facilityDetail[field.name] ?? "",
                            };
                            break;
                        case DATA_TYPES.BOOLEAN:
                            newFormData[field.name] = {
                                ...prev[field.name],
                                value: !!facilityDetail[field.name],
                            };
                            break;
                        case DATA_TYPES.STRING_ARRAY:
                            newFormData[field.name] = {
                                ...prev[field.name],
                                value: facilityDetail[field.name] ?? [],
                            };
                            break;
                        case DATA_TYPES.OBJECT:
                            newFormData[field.name] = {
                                ...prev[field.name],
                                value: facilityDetail[field.name]?.id ?? "",
                            };
                            break;
                    }
                });
                return newFormData;
            });
        }
        setMode(facilityDetail ? MODAL_MODES.UPDATE : MODAL_MODES.CREATE);
    }, [facilityDetail]);

    const [currentUser] = useCurrentUser();

    const isFacilityFieldEditable = (field) => {
        if (mode === MODAL_MODES.CREATE && field?.modes?.includes(MODAL_MODES.CREATE)) {
            return true;
        }
        if (field.selfEditable == false) {
            return false;
        }
        return field.editableByRepTypeIds.includes(currentUser.repType.id);
    };

    useBusinessUnitCodes(open, resetKey, (data) => {
        setFormData((prev) => {
            return {...prev, businessUnitCode: {...prev.businessUnitCode, options: data}};
        });
    });
    useGolferOrderActionTypes(open, resetKey, (data) => {
        setFormData((prev) => {
            return {...prev, golferOrderActionTypeName: {...prev.golferOrderActionTypeName, options: data}};
        });
    });

    useCountries(
        formData.businessUnitCode.value,
        open && isFacilityFieldEditable(FACILITY_FIELDS.find((field) => field.name === "country")),
        (data) => {
            setFormData((prev) => {
                if (mode === MODAL_MODES.UPDATE) {
                    return {...prev, country: {...prev.country, options: data}};
                } else {
                    const defaultCountry = data ? data[0]?.id : "";
                    return {
                        ...prev,
                        country: {...prev.country, options: data, value: defaultCountry},
                    };
                }
            });
        }
    );

    useStates(formData.businessUnitCode.value, formData.country.value, open, (data) => {
        setFormData((prev) => {
            if (mode === MODAL_MODES.UPDATE) {
                return {...prev, state: {...prev.state, options: data}};
            } else {
                const defaultState = data ? data[0]?.id : "";
                return {
                    ...prev,
                    state: {...prev.state, options: data, value: defaultState},
                };
            }
        });
    });
    const facilityUpdateMutation = useFacilityUpdate();
    const facilityCreateMutation = useFacilityCreate();

    const handleInputChange = (key, value) => {
        setFormData((prev) => {
            let newFormData = {...prev, [key]: {...prev[key], value: value}};
            return newFormData;
        });
    };

    const handleOpen = (facility) => {
        if (facility) {
            setFacilityDetail({...facility});
        } else {
            setFacilityDetail(null);
        }

        setOpen(true);
    };

    const handleClose = () => {
        setErrorMessages({});
        setOpen(false);
        setFormData(getInitialFormData());
        setResetKey((prev) => prev + 1);
    };

    const composePayload = () => {
        let payload = {};
        FACILITY_FIELDS.forEach((field) => {
            if (field.key && isFacilityFieldEditable(field)) {
                switch (field.dataType) {
                    case DATA_TYPES.BOOLEAN:
                        payload[field.key] = !!formData[field.name].value;
                        break;
                    case DATA_TYPES.STRING_ARRAY:
                        let filteredValue = formData[field.name].value.filter((id) =>
                            formData[field.name].options.some((option) => option.id === id)
                        );
                        if (filteredValue.length > 0) {
                            payload[field.key] = filteredValue;
                        }
                        break;
                    default:
                        if (!!formData[field.name].value) {
                            payload[field.key] = formData[field.name].value;
                        }
                        break;
                }
            }
        });
        return payload;
    };

    const onCreateSuccess = () => {
        refetchFacilities();
        handleClose();
    };

    const onUpdateSuccess = () => {
        refetchFacilities();
        handleClose();
    };

    const onSubmitError = (error) => {
        setErrorMessages({repView: error.response.data.message});
    };

    const handleSubmit = () => {
        setLoading(true);
        const payload = composePayload();
        const action = mode === MODAL_MODES.CREATE ? "Creating" : "Updating";
        try {
            if (mode === MODAL_MODES.CREATE) {
                facilityCreateMutation.mutate(payload, {
                    onSuccess: onCreateSuccess,
                    onError: onSubmitError,
                });
            } else {
                payload["id"] = facilityDetail.id;
                facilityUpdateMutation.mutate(payload, {
                    onSuccess: onUpdateSuccess,
                    onError: onSubmitError,
                });
            }
        } catch (error) {
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <Dialog
                open={open}
                fullWidth={true}
                maxWidth="md"
                PaperProps={{
                    component: "form",
                    onSubmit: (e) => {
                        e.preventDefault();
                        handleSubmit();
                    },
                }}
            >
                <DialogTitle zIndex={3}>
                    {facilityDetail && mode === MODAL_MODES.UPDATE ? `${facilityDetail.name || ""}` : "New Facility"}
                </DialogTitle>
                <DialogContent dividers={true}>
                    <Grid container spacing={1}>
                        {FACILITY_FIELDS.filter((field) => field.modes.includes(mode)).map((field) => {
                            switch (field.viewType) {
                                case VIEW_TYPES.TEXT:
                                    let value = formData[field.name].value;
                                    if (field.dataType === DATA_TYPES.TIMESTAMP) {
                                        value = value ? new Date(value).toLocaleString() : "";
                                    }
                                    return (
                                        <Grid item xs={12} key={field.name}>
                                            <MyTextField
                                                value={value}
                                                editable={isFacilityFieldEditable(field)}
                                                label={field.label}
                                                type="text"
                                                required={mode === MODAL_MODES.CREATE && field.requiredForCreate}
                                                onChange={(event) => handleInputChange(field.name, event.target.value)}
                                            />
                                        </Grid>
                                    );
                                case VIEW_TYPES.SINGLE_SELECT:
                                    let options = formData[field.name].options;
                                    if (options.length === 0) {
                                        if (field.dataType === DATA_TYPES.OBJECT) {
                                            options = [
                                                {
                                                    id: facilityDetail ? facilityDetail[field.name]?.id ?? "" : "",
                                                    name: facilityDetail ? facilityDetail[field.name]?.name ?? "" : "",
                                                },
                                            ];
                                        } else {
                                            options = [
                                                {
                                                    id: facilityDetail ? facilityDetail[field.name] ?? "" : "",
                                                    name: facilityDetail ? facilityDetail[field.name] ?? "" : "",
                                                },
                                            ];
                                        }
                                    }
                                    return (
                                        <Grid item xs={12} key={field.name}>
                                            <MySelect
                                                key={resetKey}
                                                label={field.label}
                                                options={options}
                                                value={formData[field.name].value}
                                                editable={isFacilityFieldEditable(field)}
                                                required={mode === MODAL_MODES.CREATE && field.requiredForCreate}
                                                autoSelect={formData[field.name].value}
                                                onChange={(value) => handleInputChange(field.name, value)}
                                            />
                                        </Grid>
                                    );
                                case VIEW_TYPES.MULTI_SELECT:
                                    return (
                                        <Grid item xs={12} key={field.name}>
                                            <MyMultiSelectCheckboxes
                                                title={field.label}
                                                options={formData[field.name].options}
                                                value={formData[field.name].value}
                                                editable={isFacilityFieldEditable(field)}
                                                onChange={(value) => handleInputChange(field.name, value)}
                                            />
                                        </Grid>
                                    );
                                case VIEW_TYPES.CHECKBOX:
                                    return (
                                        <Grid item xs={12} key={field.name}>
                                            <MyCheckbox
                                                checked={!!formData[field.name].value}
                                                editable={isFacilityFieldEditable(field)}
                                                label={field.label}
                                                onChange={(event) =>
                                                    handleInputChange(field.name, event.target.checked)
                                                }
                                            />
                                        </Grid>
                                    );
                            }
                        })}
                        {mode == MODAL_MODES.UPDATE && facilityDetail && (
                            <FacilityUsersTable
                                facilityId={facilityDetail?.id}
                                businessUnitCode={facilityDetail?.businessUnitCode}
                            />
                        )}
                    </Grid>
                </DialogContent>
                <DialogActions
                    sx={{
                        m: 2,
                        zIndex: 3,
                    }}
                >
                    <Button onClick={handleClose}>Cancel</Button>
                    <Button type="submit" variant="contained" disabled={loading}>
                        {mode === MODAL_MODES.CREATE ? "CREATE" : "UPDATE"}
                    </Button>
                </DialogActions>
                {errorMessages.repView && (
                    <Box mt={2}>
                        <Alert severity="error">{errorMessages.repView}</Alert>
                    </Box>
                )}
            </Dialog>
        </>
    );
});

export default FacilityModal;
